import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Badge,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  ChatBubbleOutline,
  Send,
  Close,
  Person,
  Store,
  AdminPanelSettings,
  Message,
  Group,
  SignalWifiConnectedNoInternet4,
  PhotoCamera,
  ExpandMore,
  ContentCopy,
} from '@mui/icons-material';
import apiClient from '../utils/apiClient';
import { useChat } from '../contexts/ChatContext';
import imageUploadService from '../services/imageUpload.service';
import { ResponsiveTable, ResponsiveModal } from '../components/common';
import type { Column } from '../components/common';
import { API_ORIGIN } from '../config/env';

interface ChatParticipant {
  userId: {
    _id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
  };
  role: 'buyer' | 'seller' | 'admin';
  joinedAt: string;
  isActive: boolean;
  unreadCount: number;
}

interface ChatMessage {
  _id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderRole: 'buyer' | 'seller' | 'admin';
  messageType: 'text' | 'image' | 'file';
  sentAt: string;
  isRead: boolean;
  isSystemMessage?: boolean;
  systemData?: {
    action: string;
    metadata?: any;
  };
  file?: {
    originalName: string;
    filename: string;
    url?: string;
    mimetype: string;
    size: number;
    base64Data?: string;
  };
}

interface DisputeChat {
  _id: string;
  type: string;
  status: string;
  title: string;
  description: string;
  participants: ChatParticipant[];
  orderId?: {
    _id: string;
    orderNumber: string;
  };
  orderItemId?: {
    _id: string;
    productTitle: string;
    disputeInfo?: {
      status: string;
      reason: string;
      description: string;
      resolution?: string;
      resolvedAt?: string;
    };
  };
  totalMessages: number;
  lastMessage?: {
    content: string;
    senderId: string;
    sentAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

const ChatManagement: React.FC = () => {
  const [disputeChats, setDisputeChats] = useState<DisputeChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<DisputeChat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Add new state for local changes
  const [localChatStatus, setLocalChatStatus] = useState<string>('');
  const [localOrderStatus, setLocalOrderStatus] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [chatHistory, setChatHistory] = useState<DisputeChat[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation() as any;
  const consumedOpenRef = useRef<string | null>(null);

  const {
    state,
    sendMessage: sendSocketMessage,
    setActiveChat,
    setMessages: setSocketMessages,
    joinChat,
    leaveChat,
  } = useChat();

  // Admin close options (penalties & restrictions)
  const [restrictBuyer, setRestrictBuyer] = useState(false);
  const [restrictSeller, setRestrictSeller] = useState(false);
  const [buyerPenaltyPoints, setBuyerPenaltyPoints] = useState<number>(0);
  const [sellerPenaltyPoints, setSellerPenaltyPoints] = useState<number>(0);
  // removed restriction date per requirement
  const [moderateAction, setModerateAction] = useState<
    | 'cancel_tx'
    | 'resolve_tx'
    | 'force_complete'
    | 'approve_dispute'
    | 'reject_dispute'
    | 'close_dispute'
    | 'resolve_dispute'
  >('cancel_tx');

  useEffect(() => {
    fetchDisputeChats();
    fetchChatHistory();
  }, []);

  // Re-fetch history when filter changes
  useEffect(() => {
    if (activeTab === 1) {
      // Only fetch when on history tab
      fetchChatHistory();
    }
  }, [selectedStatusFilter, activeTab]);

  // Auto-open a specific chat if requested via navigation state
  useEffect(() => {
    const openId: string | undefined = location?.state?.openChatId;
    if (!openId || consumedOpenRef.current === openId) return;

    const inActive = (disputeChats || []).find((c) => c._id === openId);
    if (inActive) {
      setActiveTab(0);
      handleOpenChat(inActive);
      consumedOpenRef.current = openId;
      return;
    }

    const inHistory = (chatHistory || []).find((c) => c._id === openId);
    if (inHistory) {
      setActiveTab(1);
      handleOpenChat(inHistory);
      consumedOpenRef.current = openId;
    }
  }, [location?.state, disputeChats, chatHistory]);

  // Fetch order status when selectedChat changes
  useEffect(() => {
    const fetchOrderStatus = async () => {
      if (selectedChat?.orderId) {
        try {
          const response = await apiClient.get(`/admin/orders/${selectedChat.orderId._id}`);
          const status = response.data.data.status || 'pending';
          setOrderStatus(status);
          setLocalOrderStatus(status);
        } catch (error) {
          console.error('Error fetching order status:', error);
          setOrderStatus('pending');
          setLocalOrderStatus('pending');
        }
      } else {
        setOrderStatus('');
        setLocalOrderStatus('');
      }

      // Reset local chat status when selectedChat changes
      if (selectedChat) {
        setLocalChatStatus(selectedChat.status);
      }
      setHasUnsavedChanges(false);
    };

    fetchOrderStatus();
  }, [selectedChat]);

  // Socket.IO event listeners
  useEffect(() => {
    const socket = state.socket;
    if (!socket) return;

    // Get current admin user ID
    const getCurrentUserId = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.id;
      } catch {
        return null;
      }
    };

    // Handle new messages
    const handleNewMessage = (data: any) => {
      console.log('Received new message:', data);

      // Handle both formats: {message, chatId} and {message, chatUpdate}
      const message = data.message || data;
      const chatId = data.chatId || (data.chatUpdate && data.chatUpdate._id);

      // Debug: Check real-time message dates
      if (message) {
        console.log('Real-time message date debug:', {
          id: message._id,
          sentAt: message.sentAt,
          createdAt: message.createdAt,
          sentAtType: typeof message.sentAt,
          createdAtType: typeof message.createdAt,
        });
      }

      if (selectedChat && message && message._id && chatId === selectedChat._id) {
        const currentUserId = getCurrentUserId();

        // Skip messages from current admin if they were already added via API
        if (currentUserId && message.senderId === currentUserId) {
          console.log('Skipping own message from Socket.IO:', message._id);
          return;
        }

        // Check for duplicate messages before adding
        setMessages((prev) => {
          const existingMessage = prev.find((m) => m._id === message._id);
          if (existingMessage) {
            console.log('Duplicate message detected, skipping:', message._id);
            return prev; // Don't add duplicate
          }
          return [...prev, message];
        });
      }
    };

    // Handle chat closed
    const handleChatClosed = (data: any) => {
      console.log('Chat closed:', data);

      // Always refresh chat list when any chat is closed
      fetchDisputeChats();

      // If the closed chat is currently selected, show message and close dialog
      if (selectedChat && data.chatId === selectedChat._id) {
        setError(`چت بسته شد: ${data.reason}`);
        // Optionally close the chat dialog
        setTimeout(() => {
          handleCloseChat();
        }, 2000);
      }
    };

    // Handle chat created
    const handleChatCreated = (data: any) => {
      console.log('New chat created:', data);

      // Always refresh chat list when new chat is created
      fetchDisputeChats();
      fetchChatHistory(); // Also refresh history

      // Show notification
      setError('چت جدید ایجاد شد');
      setTimeout(() => setError(''), 3000);
    };

    socket.on('new_message', handleNewMessage);
    socket.on('chat_closed', handleChatClosed);
    socket.on('chat_created', handleChatCreated);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('chat_closed', handleChatClosed);
      socket.off('chat_created', handleChatCreated);
    };
  }, [state.socket, selectedChat]);

  // Handle real-time status changes
  useEffect(() => {
    const socket = state.socket;
    if (!socket) return;

    const handleChatStatusChanged = (data: any) => {
      console.log('Chat status changed:', data);

      // Update selected chat status if it matches
      if (selectedChat && selectedChat._id === data.chatId) {
        setSelectedChat((prev) => (prev ? { ...prev, status: data.newStatus } : null));
      }

      // Add system message to chat if provided
      if (data.systemMessage && selectedChat && selectedChat._id === data.chatId) {
        setMessages((prev) => {
          const existingMessage = prev.find((m) => m._id === data.systemMessage._id);
          if (existingMessage) {
            return prev;
          }
          return [...prev, data.systemMessage];
        });
      }

      // Show notification
      setError(`وضعیت چت تغییر کرد: ${data.message}`);
      setTimeout(() => setError(''), 3000);

      // Refresh chat list
      fetchDisputeChats();
    };

    socket.on('chat_status_changed', handleChatStatusChanged);

    return () => {
      socket.off('chat_status_changed', handleChatStatusChanged);
    };
  }, [state.socket, selectedChat]);

  // Handle local chat status change (no immediate API call)
  const handleLocalChatStatusChange = (newStatus: string) => {
    setLocalChatStatus(newStatus);
    setHasUnsavedChanges(newStatus !== selectedChat?.status || localOrderStatus !== orderStatus);
  };

  // Handle local order status change (no immediate API call)
  const handleLocalOrderStatusChange = (newStatus: string) => {
    setLocalOrderStatus(newStatus);
    setHasUnsavedChanges(localChatStatus !== selectedChat?.status || newStatus !== orderStatus);
  };

  // Apply all changes with API calls
  const handleApplyChanges = async () => {
    if (!selectedChat || updatingStatus || !hasUnsavedChanges) return;

    // Validate required status message
    if (!statusMessage.trim()) {
      setError('نوشتن دلیل تغییر اجباری است');
      return;
    }

    // Extra validation for refund
    if (localOrderStatus === 'refunded' && localOrderStatus !== orderStatus) {
      if (!statusMessage.trim()) {
        setError('برای بازپرداخت سفارش، نوشتن دلیل الزامی است');
        return;
      }
    }

    try {
      setUpdatingStatus(true);

      // Update chat status if changed
      if (localChatStatus !== selectedChat.status) {
        await apiClient.put(`/admin/chats/${selectedChat._id}/status`, {
          status: localChatStatus,
          adminMessage: statusMessage.trim(),
        });

        // Update selected chat status
        setSelectedChat((prev) => (prev ? { ...prev, status: localChatStatus } : null));
      }

      // Update order status if changed
      if (localOrderStatus !== orderStatus && selectedChat.orderId) {
        // If changing to refunded, use the refund API instead of status update
        if (localOrderStatus === 'refunded') {
          const refundResponse = await apiClient.post(`/admin/orders/${selectedChat.orderId._id}/refund`, {
            reason: statusMessage.trim() || 'بازپرداخت از طریق مدیریت چت',
          });

          // Check wallet refund status
          if (refundResponse.data?.data?.walletUpdated) {
            console.log('✅ Order refunded successfully and amount credited to user wallet');
            // You can show a success toast here
          } else {
            console.warn('⚠️ Order refunded but wallet update failed:', refundResponse.data?.data?.walletError);
            setError(
              `هشدار: سفارش بازپرداخت شد اما خطا در واریز به کیف پول: ${
                refundResponse.data?.data?.walletError || 'خطای نامشخص'
              }`
            );
          }
        } else {
          // For other status changes, use the regular status update API
          await apiClient.put(`/admin/orders/${selectedChat.orderId._id}/status`, {
            status: localOrderStatus,
          });
        }

        setOrderStatus(localOrderStatus);
      }

      // Clear error for non-refund operations
      if (localOrderStatus !== 'refunded') {
        setError(''); // Clear any previous errors
      }

      // Clear status message and reset unsaved changes
      setStatusMessage('');
      setHasUnsavedChanges(false);

      // Refresh chat list
      fetchDisputeChats();
    } catch (error) {
      console.error('Error applying changes:', error);
      setError('خطا در اعمال تغییرات');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Cancel changes and revert to original values
  const handleCancelChanges = () => {
    if (selectedChat) {
      setLocalChatStatus(selectedChat.status);
    }
    setLocalOrderStatus(orderStatus);
    setStatusMessage('');
    setHasUnsavedChanges(false);
  };

  const fetchDisputeChats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/chats/disputes');
      console.log('Dispute chats API response:', response.data.data);
      response.data.data.forEach((chat: any, index: number) => {
        console.log(`Dispute Chat ${index + 1} participants:`, chat.participants);
        chat.participants?.forEach((participant: any) => {
          console.log(`Participant:`, {
            role: participant.role,
            userId: participant.userId,
            storeName: participant.userId?.storeName,
          });
        });
      });
      setDisputeChats(response.data.data);
    } catch (error) {
      console.error('Error fetching dispute chats:', error);
      setError('خطا در دریافت چت‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await apiClient.get('/admin/chats/history', {
        params: {
          status: selectedStatusFilter === 'all' ? undefined : selectedStatusFilter,
          limit: 100, // Get more records for history
        },
      });
      console.log('Chat history API response:', response.data.data);
      response.data.data.forEach((chat: any, index: number) => {
        console.log(`History Chat ${index + 1} participants:`, chat.participants);
        chat.participants?.forEach((participant: any) => {
          console.log(`History Participant:`, {
            role: participant.role,
            userId: participant.userId,
            storeName: participant.userId?.storeName,
          });
        });
      });
      if (response.data.status === 'success') {
        setChatHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setError('خطا در دریافت تاریخچه چت‌ها');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchChatMessages = async (chatId: string) => {
    try {
      const response = await apiClient.get(`/admin/chats/${chatId}/messages`);
      const messages = response.data.data;

      // Debug: Check message dates
      console.log(
        'Fetched messages:',
        messages.map((msg: any) => ({
          id: msg._id,
          content: msg.content?.substring(0, 20),
          sentAt: msg.sentAt,
          createdAt: msg.createdAt,
          sentAtType: typeof msg.sentAt,
          createdAtType: typeof msg.createdAt,
        }))
      );

      setMessages(messages);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      setError('خطا در دریافت پیام‌ها');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        // Validate file
        const { validFiles, errors } = imageUploadService.validateFiles([file], { maxSize: 5 * 1024 * 1024 });
        if (errors.length > 0) {
          alert(errors.join('\n'));
          return;
        }

        setSelectedFile(file);

        // Show preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // Upload to ArvanCloud
        try {
          setUploadingImage(true);
          const result = await imageUploadService.uploadImage(file, 'chat');
          if (result) {
            setUploadedImageUrl(result.url);
            console.log('Image uploaded to ArvanCloud:', result.url);
          } else {
            alert('خطا در آپلود تصویر به سرور');
            clearSelectedFile();
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          alert('خطا در آپلود تصویر');
          clearSelectedFile();
        } finally {
          setUploadingImage(false);
        }
      } else {
        alert('لطفاً فقط فایل‌های تصویری انتخاب کنید');
      }
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !uploadedImageUrl) || !selectedChat) return;

    // Get current admin info
    const getCurrentAdmin = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return {
          id: user.id || user._id,
          name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'ادمین',
        };
      } catch {
        return { id: 'admin', name: 'ادمین' };
      }
    };

    const admin = getCurrentAdmin();
    const messageContent = newMessage.trim();
    const messageType = uploadedImageUrl ? 'image' : 'text';

    // Create optimistic message with temp ID for instant display
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const optimisticMessage: ChatMessage = {
      _id: tempId,
      content: messageContent,
      senderId: admin.id,
      senderName: admin.name,
      senderRole: 'admin',
      messageType: messageType,
      sentAt: new Date().toISOString(),
      isRead: false,
      ...(uploadedImageUrl && {
        file: {
          originalName: 'image',
          filename: 'image',
          url: uploadedImageUrl,
          mimetype: 'image/jpeg',
          size: 0,
        },
      }),
    };

    // Add optimistic message immediately for instant feedback
    setMessages((prev) => [...prev, optimisticMessage]);

    // Clear input immediately
    const savedMessage = newMessage;
    const savedImageUrl = uploadedImageUrl;
    setNewMessage('');
    clearSelectedFile();

    try {
      setSendingMessage(true);

      // Send via API
      const response = await apiClient.post(`/admin/chats/${selectedChat._id}/messages`, {
        content: savedMessage,
        messageType: messageType,
        imageUrl: savedImageUrl,
      });

      // Replace optimistic message with real message from server
      if (response.data.status === 'success') {
        const realMessage = response.data.data;
        setMessages((prev) => {
          // Remove temp message and add real message (avoiding duplicates)
          const filtered = prev.filter((m) => m._id !== tempId);
          const exists = filtered.some((m) => m._id === realMessage._id);
          return exists ? filtered : [...filtered, realMessage];
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('خطا در ارسال پیام');
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      // Restore message to input
      setNewMessage(savedMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenChat = (chat: DisputeChat) => {
    console.log('Selected chat:', chat);
    console.log('Selected chat participants:', chat.participants);
    chat.participants?.forEach((participant: any) => {
      console.log(`Selected chat participant:`, {
        role: participant.role,
        userId: participant.userId,
        userFullName: participant.userId ? `${participant.userId.firstName} ${participant.userId.lastName}` : 'N/A',
        email: participant.userId?.email,
        storeName: participant.userId?.storeName,
      });
    });

    // Leave previous chat room if any
    if (selectedChat && selectedChat._id !== chat._id) {
      leaveChat(selectedChat._id);
    }

    // Join the new chat room for real-time updates
    joinChat(chat._id);

    setSelectedChat(chat);
    setChatDialogOpen(true);
    setActiveChat(chat);
    fetchChatMessages(chat._id);
  };

  const handleCloseChat = () => {
    // Leave the chat room
    if (selectedChat) {
      leaveChat(selectedChat._id);
    }

    setChatDialogOpen(false);
    setSelectedChat(null);
    setMessages([]);
    clearSelectedFile();
    setNewMessage('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'dispute':
        return 'warning';
      case 'closed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'فعال';
      case 'dispute':
        return 'اختلاف';
      case 'closed':
        return 'بسته';
      default:
        return status;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'buyer':
        return 'خریدار';
      case 'seller':
        return 'فروشنده';
      case 'admin':
        return 'ادمین';
      default:
        return role;
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'buyer':
        return <Person />;
      case 'seller':
        return <Store />;
      case 'admin':
        return <AdminPanelSettings />;
      default:
        return <Person />;
    }
  };

  const formatDate = (dateString: string | Date | undefined | null) => {
    if (!dateString) {
      console.warn('formatDate called with empty value:', dateString);
      return 'نامشخص';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('formatDate: Invalid date value:', dateString, typeof dateString);
        return 'تاریخ نامعتبر';
      }

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', dateString, typeof dateString, error);
      return 'خطا در تاریخ';
    }
  };

  const getUserDisplayName = (user: { firstName?: string; lastName?: string; name?: string }) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.name || 'کاربر';
  };

  const getFilteredChatHistory = () => {
    if (selectedStatusFilter === 'all') {
      return chatHistory;
    }
    return chatHistory.filter((chat) => chat.status === selectedStatusFilter);
  };

  const getChatHistoryStats = () => {
    const stats = {
      all: chatHistory.length,
      active: chatHistory.filter((chat) => chat.status === 'active').length,
      dispute: chatHistory.filter((chat) => chat.status === 'dispute').length,
      closed: chatHistory.filter((chat) => chat.status === 'closed').length,
    };
    return stats;
  };

  const renderMessage = (message: ChatMessage) => {
    const isImage = message.messageType === 'image' && message.file;
    const isSystemMessage = message.isSystemMessage || (message as any).systemData;

    // System messages (status changes)
    if (isSystemMessage) {
      return (
        <ListItem key={message._id} divider>
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              📢 اعلان سیستم
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {message.content}
            </Typography>
            <Typography variant="caption" color="warning.dark">
              توسط ادمین - {formatDate(message.sentAt)}
            </Typography>
          </Box>
        </ListItem>
      );
    }

    // Regular messages
    return (
      <ListItem key={message._id} divider>
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor:
                message.senderRole === 'admin' ? '#9c27b0' : message.senderRole === 'seller' ? '#ff9800' : '#2196f3',
            }}
          >
            {getRoleIcon(message.senderRole)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={`${message.senderName || 'کاربر'} (${getRoleLabel(message.senderRole)})`}
          secondary={
            <Box>
              {/* Text content */}
              {message.content && (
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                  {message.content}
                </Typography>
              )}

              {/* Image content */}
              {isImage &&
                (() => {
                  // Get image URL - handle ArvanCloud URLs, base64, and local paths
                  const getImageUrl = () => {
                    if (message.file!.base64Data) return message.file!.base64Data;
                    if (message.file!.url) {
                      // If it's a full URL (ArvanCloud), use it directly
                      if (message.file!.url.startsWith('http://') || message.file!.url.startsWith('https://')) {
                        return message.file!.url;
                      }
                      // Otherwise, prepend local server URL
                      return `${API_ORIGIN}${
                        message.file!.url
                      }`;
                    }
                    return '';
                  };
                  const imageUrl = getImageUrl();

                  return (
                    <Box sx={{ mb: 1 }}>
                      <img
                        src={imageUrl}
                        alt={message.file!.originalName}
                        style={{
                          maxWidth: '300px',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(imageUrl, '_blank')}
                      />
                    </Box>
                  );
                })()}

              <Typography variant="caption" display="block" color="text.secondary">
                {formatDate(message.sentAt)}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  };

  // Columns for the active dispute chats table
  const disputeColumns: Column<DisputeChat>[] = [
    {
      id: 'title',
      label: 'عنوان چت',
      priority: 1,
      format: (v) => <Typography variant="body2">{v}</Typography>,
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 2,
      format: (v: string) => <Chip label={getStatusLabel(v)} color={getStatusColor(v) as any} size="small" />,
    },
    {
      id: 'participants',
      label: 'شرکت‌کنندگان',
      priority: 4,
      format: (_v, chat) => (
        <Box display="flex" gap={1} flexWrap="wrap">
          {chat.participants.map((participant) => (
            <Chip
              key={participant.userId._id}
              avatar={<Avatar>{getRoleIcon(participant.role)}</Avatar>}
              label={`${getUserDisplayName(participant.userId)} (${getRoleLabel(participant.role)})`}
              size="small"
            />
          ))}
        </Box>
      ),
    },
    {
      id: 'totalMessages',
      label: 'تعداد پیام‌ها',
      priority: 3,
    },
    {
      id: 'lastMessage',
      label: 'آخرین پیام',
      priority: 5,
      detail: true,
      format: (_v, chat) =>
        chat.lastMessage ? (
          <Box>
            <Typography variant="caption" display="block">
              {chat.lastMessage.content}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(chat.lastMessage.sentAt)}
            </Typography>
          </Box>
        ) : (
          '-'
        ),
    },
  ];

  const renderDisputeActions = (chat: DisputeChat) => (
    <Button variant="outlined" size="small" startIcon={<ChatBubbleOutline />} onClick={() => handleOpenChat(chat)}>
      مشاهده چت
    </Button>
  );

  // Columns for the chat history table
  const historyColumns: Column<DisputeChat>[] = [
    {
      id: 'title',
      label: 'عنوان چت',
      priority: 1,
      format: (_v, chat) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {chat.title || 'چت بدون عنوان'}
          </Typography>
          {chat.orderItemId?.productTitle && (
            <Typography variant="caption" color="text.secondary">
              محصول: {chat.orderItemId.productTitle}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'participants',
      label: 'شرکت‌کنندگان',
      priority: 4,
      format: (_v, chat) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {chat.participants.map((participant, index) => (
            <Chip
              key={index}
              size="small"
              label={getRoleLabel(participant.role)}
              color={participant.role === 'admin' ? 'secondary' : participant.role === 'seller' ? 'warning' : 'primary'}
            />
          ))}
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 2,
      format: (v: string) => (
        <Chip
          size="small"
          label={v === 'active' ? 'فعال' : v === 'dispute' ? 'اختلاف' : 'بسته'}
          color={v === 'active' ? 'success' : v === 'dispute' ? 'error' : 'default'}
        />
      ),
    },
    {
      id: 'totalMessages',
      label: 'تعداد پیام‌ها',
      priority: 3,
      format: (v: number) => (
        <Badge badgeContent={v} color="primary">
          <ChatBubbleOutline />
        </Badge>
      ),
    },
    {
      id: 'updatedAt',
      label: 'آخرین فعالیت',
      priority: 5,
      detail: true,
      format: (v: string) => <Typography variant="caption">{formatDate(v)}</Typography>,
    },
  ];

  const renderHistoryActions = (chat: DisputeChat) => (
    <Button size="small" variant="outlined" startIcon={<ChatBubbleOutline />} onClick={() => handleOpenChat(chat)}>
      مشاهده
    </Button>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        مدیریت چت‌ها
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="چت‌های فعال (اختلاف)" />
          <Tab label="تاریخچه چت‌ها" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 ? (
        // Active Dispute Chats Tab
        loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Stats Cards */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Group color="primary" />
                    <Box>
                      <Typography variant="h6">{disputeChats.length}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        کل چت‌ها
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <SignalWifiConnectedNoInternet4 color="warning" />
                    <Box>
                      <Typography variant="h6">
                        {disputeChats.filter((chat) => chat.status === 'dispute').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        اختلافات فعال
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Message color="info" />
                    <Box>
                      <Typography variant="h6">
                        {disputeChats.reduce((total, chat) => total + chat.totalMessages, 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        کل پیام‌ها
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chat List */}
            <Grid item xs={12}>
              <ResponsiveTable<DisputeChat>
                columns={disputeColumns}
                data={disputeChats}
                keyField="_id"
                emptyMessage="چتی یافت نشد"
                renderActions={renderDisputeActions}
              />
            </Grid>
          </Grid>
        )
      ) : // Chat History Tab
      loadingHistory ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* History Filter */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h6">فیلتر بر اساس وضعیت:</Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)}>
                    <MenuItem value="all">همه ({getChatHistoryStats().all})</MenuItem>
                    <MenuItem value="active">فعال ({getChatHistoryStats().active})</MenuItem>
                    <MenuItem value="dispute">اختلاف ({getChatHistoryStats().dispute})</MenuItem>
                    <MenuItem value="closed">بسته ({getChatHistoryStats().closed})</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Grid>

          {/* History List */}
          <Grid item xs={12}>
            <ResponsiveTable<DisputeChat>
              columns={historyColumns}
              data={getFilteredChatHistory()}
              keyField="_id"
              emptyMessage="چتی با این فیلتر یافت نشد"
              renderActions={renderHistoryActions}
            />
          </Grid>
        </Grid>
      )}

      {/* Chat Dialog */}
      <ResponsiveModal open={chatDialogOpen} onClose={handleCloseChat} title={selectedChat?.title} maxWidth="md">
        <Box>
          {/* Chat & Order Management Section */}
          {selectedChat && (
            <Accordion sx={{ mb: 3, borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 48 },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="h6" fontWeight="600">
                    🛠️ مدیریت چت و سفارش
                  </Typography>
                  {hasUnsavedChanges && <Chip label="تغییرات ذخیره نشده" color="warning" size="small" sx={{ mr: 2 }} />}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 4 }}>
                {/* Chat & Order Information */}
                <Box sx={{ mb: 4 }}>
                  {/* Seller Info */}
                  {selectedChat.participants?.find((p) => p.role === 'seller') ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
                        فروشگاه:{' '}
                        {(selectedChat.participants.find((p) => p.role === 'seller')?.userId as any)?.storeName ||
                          'نام فروشگاه در دسترس نیست'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        فروشنده: {selectedChat.participants.find((p) => p.role === 'seller')?.userId?.firstName}{' '}
                        {selectedChat.participants.find((p) => p.role === 'seller')?.userId?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedChat.participants.find((p) => p.role === 'seller')?.userId?.email}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      اطلاعات فروشنده در دسترس نیست
                    </Typography>
                  )}

                  {/* Buyer Info */}
                  {selectedChat.participants?.find((p) => p.role === 'buyer') ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
                        خریدار: {selectedChat.participants.find((p) => p.role === 'buyer')?.userId?.firstName}{' '}
                        {selectedChat.participants.find((p) => p.role === 'buyer')?.userId?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedChat.participants.find((p) => p.role === 'buyer')?.userId?.email}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      اطلاعات خریدار در دسترس نیست
                    </Typography>
                  )}

                  {/* Admin Info */}
                  {selectedChat.participants?.find((p) => p.role === 'admin') && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
                        ادمین: {selectedChat.participants.find((p) => p.role === 'admin')?.userId?.firstName}{' '}
                        {selectedChat.participants.find((p) => p.role === 'admin')?.userId?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedChat.participants.find((p) => p.role === 'admin')?.userId?.email}
                      </Typography>
                    </Box>
                  )}

                  {/* Order Info */}
                  {selectedChat.orderId && (
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="500">
                        شماره سفارش: {selectedChat.orderId.orderNumber}
                      </Typography>
                      <Tooltip title="کپی شماره سفارش">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedChat.orderId?.orderNumber || '');
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* Product Info */}
                {selectedChat.orderItemId && (
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                      🎮 محصول
                    </Typography>
                    <Typography variant="body2">{selectedChat.orderItemId.productTitle}</Typography>
                  </Box>
                )}

                {/* Status Controls */}
                <Box display="flex" flexDirection="column" gap={3}>
                  {/* Chat and Order Status Row */}
                  <Box display="flex" gap={3}>
                    {/* Chat Status */}
                    <Box flex={1}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="500">
                        وضعیت چت
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={localChatStatus}
                          onChange={(e) => handleLocalChatStatusChange(e.target.value)}
                          disabled={updatingStatus}
                        >
                          <MenuItem value="active">فعال</MenuItem>
                          <MenuItem value="dispute">اختلاف</MenuItem>
                          <MenuItem value="closed">بسته</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Order Status */}
                    {selectedChat.orderId && (
                      <Box flex={1}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="500">
                          وضعیت سفارش
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={localOrderStatus}
                            onChange={(e) => handleLocalOrderStatusChange(e.target.value)}
                            disabled={updatingStatus || orderStatus === 'cancelled' || orderStatus === 'refunded'}
                          >
                            <MenuItem value="pending">در انتظار</MenuItem>
                            <MenuItem value="confirmed">تایید شده</MenuItem>
                            <MenuItem value="processing">در حال پردازش</MenuItem>
                            <MenuItem value="shipped">ارسال شده</MenuItem>
                            <MenuItem value="delivered">تحویل داده شده</MenuItem>
                            <MenuItem value="cancelled">لغو شده</MenuItem>
                            <MenuItem value="refunded">مرجوع شده</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Refund Warning */}
                        {localOrderStatus === 'refunded' && localOrderStatus !== orderStatus && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            ⚠️ بازپرداخت: مبلغ به کیف پول کاربر واریز می‌شود و از حساب فروشنده کسر می‌شود
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Message */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="500">
                      دلیل تغییر <Chip label="اجباری" size="small" color="error" />
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="دلیل تغییر وضعیت را به طور کامل بنویسید..."
                      value={statusMessage}
                      onChange={(e) => {
                        setStatusMessage(e.target.value);
                        if (e.target.value.trim()) {
                          setError(null);
                        }
                      }}
                      disabled={updatingStatus}
                      error={!statusMessage.trim() && hasUnsavedChanges}
                      inputProps={{ maxLength: 500 }}
                    />
                  </Box>

                  {/* Admin Close + penalties/restrictions */}
                  <Box display="flex" flexDirection="column" gap={1} sx={{ my: 2 }}>
                    <Typography variant="subtitle2">اقدامات هنگام بستن چت</Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox checked={restrictBuyer} onChange={(e) => setRestrictBuyer(e.target.checked)} />
                        }
                        label="محدودیت برای خریدار"
                      />
                      <TextField
                        type="number"
                        size="small"
                        label="کسر امتیاز خریدار"
                        value={buyerPenaltyPoints}
                        onChange={(e) => setBuyerPenaltyPoints(Number(e.target.value))}
                        sx={{ width: 180 }}
                      />
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox checked={restrictSeller} onChange={(e) => setRestrictSeller(e.target.checked)} />
                        }
                        label="محدودیت برای فروشنده"
                      />
                      <TextField
                        type="number"
                        size="small"
                        label="کسر امتیاز فروشنده"
                        value={sellerPenaltyPoints}
                        onChange={(e) => setSellerPenaltyPoints(Number(e.target.value))}
                        sx={{ width: 180 }}
                      />
                    </Box>
                    <Box>
                      {/* Moderation actions */}
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        {(
                          [
                            'cancel_tx',
                            'approve_dispute',
                            'reject_dispute',
                            'resolve_dispute',
                            'close_dispute',
                            'resolve_tx',
                            'force_complete',
                          ] as const
                        ).map((act) => (
                          <Chip
                            key={act}
                            label={
                              act === 'cancel_tx'
                                ? 'لغو تراکنش'
                                : act === 'approve_dispute'
                                ? 'تأیید اختلاف'
                                : act === 'reject_dispute'
                                ? 'رد اختلاف'
                                : act === 'resolve_dispute'
                                ? 'حل اختلاف'
                                : act === 'close_dispute'
                                ? 'بستن اختلاف'
                                : act === 'resolve_tx'
                                ? 'حل تراکنش'
                                : 'تکمیل اجباری'
                            }
                            color={moderateAction === act ? 'primary' : 'default'}
                            size="small"
                            variant={moderateAction === act ? 'filled' : 'outlined'}
                            onClick={() => setModerateAction(act)}
                          />
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={async () => {
                            if (!selectedChat) return;
                            const penalties: any[] = [];
                            const restrictions: any[] = [];
                            const buyer = selectedChat.participants.find((p) => p.role === 'buyer')?.userId?._id;
                            const seller = selectedChat.participants.find((p) => p.role === 'seller')?.userId?._id;
                            if (buyerPenaltyPoints > 0 && buyer)
                              penalties.push({ userId: buyer, points: buyerPenaltyPoints });
                            if (sellerPenaltyPoints > 0 && seller)
                              penalties.push({ userId: seller, points: sellerPenaltyPoints });
                            if (restrictBuyer && buyer) restrictions.push({ userId: buyer, restrict: true });
                            if (restrictSeller && seller) restrictions.push({ userId: seller, restrict: true });
                            try {
                              await apiClient.put(`/admin/chats/${selectedChat._id}/close`, {
                                reason: statusMessage || 'بستن چت توسط ادمین',
                                penalties,
                                restrictions,
                              });
                              setRestrictBuyer(false);
                              setRestrictSeller(false);
                              setBuyerPenaltyPoints(0);
                              setSellerPenaltyPoints(0);
                              handleCloseChat();
                              fetchDisputeChats();
                              fetchChatHistory();
                            } catch (err) {
                              console.error('Error closing chat with penalties:', err);
                            }
                          }}
                        >
                          بستن چت + اعمال اقدامات
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={async () => {
                            if (!selectedChat) return;
                            const penalties: any[] = [];
                            const restrictions: any[] = [];
                            const buyer = selectedChat.participants.find((p) => p.role === 'buyer')?.userId?._id;
                            const seller = selectedChat.participants.find((p) => p.role === 'seller')?.userId?._id;
                            if (buyerPenaltyPoints > 0 && buyer)
                              penalties.push({ userId: buyer, points: buyerPenaltyPoints });
                            if (sellerPenaltyPoints > 0 && seller)
                              penalties.push({ userId: seller, points: sellerPenaltyPoints });
                            if (restrictBuyer && buyer) restrictions.push({ userId: buyer, restrict: true });
                            if (restrictSeller && seller) restrictions.push({ userId: seller, restrict: true });
                            try {
                              await apiClient.post(`/admin/chats/${selectedChat._id}/moderate`, {
                                action: moderateAction,
                                reason: statusMessage || 'اقدام داوری',
                                penalties,
                                restrictions,
                              });
                              setRestrictBuyer(false);
                              setRestrictSeller(false);
                              setBuyerPenaltyPoints(0);
                              setSellerPenaltyPoints(0);
                              handleCloseChat();
                              fetchDisputeChats();
                              fetchChatHistory();
                            } catch (err: any) {
                              const msg = err?.response?.data?.message || err?.message || 'خطا در اعمال داوری';
                              console.error('Error moderating tx via chat:', err);
                              alert(`⚠️ ${msg}`);
                            }
                          }}
                        >
                          اعمال داوری
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={async () => {
                            if (!selectedChat) return;
                            const penalties: any[] = [];
                            const restrictions: any[] = [];
                            const buyer = selectedChat.participants.find((p) => p.role === 'buyer')?.userId?._id;
                            const seller = selectedChat.participants.find((p) => p.role === 'seller')?.userId?._id;
                            if (buyerPenaltyPoints > 0 && buyer)
                              penalties.push({ userId: buyer, points: buyerPenaltyPoints });
                            if (sellerPenaltyPoints > 0 && seller)
                              penalties.push({ userId: seller, points: sellerPenaltyPoints });
                            if (restrictBuyer && buyer) restrictions.push({ userId: buyer, restrict: true });
                            if (restrictSeller && seller) restrictions.push({ userId: seller, restrict: true });
                            try {
                              await apiClient.post(`/admin/chats/${selectedChat._id}/moderate`, {
                                action: 'cancel_tx',
                                reason: statusMessage || 'لغو تراکنش توسط ادمین',
                                penalties,
                                restrictions,
                              });
                              setRestrictBuyer(false);
                              setRestrictSeller(false);
                              setBuyerPenaltyPoints(0);
                              setSellerPenaltyPoints(0);
                              handleCloseChat();
                              fetchDisputeChats();
                              fetchChatHistory();
                            } catch (err) {
                              console.error('Error moderating tx via chat:', err);
                            }
                          }}
                        >
                          لغو تراکنش (داوری)
                        </Button>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancelChanges}
                      disabled={updatingStatus || !hasUnsavedChanges}
                    >
                      لغو تغییرات
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleApplyChanges}
                      disabled={updatingStatus || !hasUnsavedChanges || !statusMessage.trim()}
                      startIcon={updatingStatus ? <CircularProgress size={16} /> : null}
                    >
                      {updatingStatus ? 'در حال اعمال...' : 'اعمال تغییرات'}
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Messages List */}
          <Box sx={{ height: '400px', overflowY: 'auto', mb: 2 }}>
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                هیچ پیامی وجود ندارد
              </Typography>
            ) : (
              <List>{messages.map(renderMessage)}</List>
            )}
          </Box>

          {/* Image Preview */}
          {imagePreview && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">تصویر انتخاب شده:</Typography>
                <IconButton size="small" onClick={clearSelectedFile}>
                  <Close />
                </IconButton>
              </Box>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '150px',
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}

          {/* Chat Status Warning */}
          {selectedChat && selectedChat.status === 'closed' && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                ⚠️ توجه: این چت برای کاربران بسته شده است
              </Typography>
              <Typography variant="caption" color="text.secondary">
                شما به عنوان ادمین همچنان می‌توانید پیام ارسال کنید
              </Typography>
            </Box>
          )}

          {/* Message Input */}
          <Box display="flex" gap={1} alignItems="flex-end">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="پیام خود را بنویسید..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              multiline
              maxRows={3}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={sendingMessage}>
                      <PhotoCamera />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              color="primary"
              onClick={sendMessage}
              disabled={(!newMessage.trim() && !uploadedImageUrl) || sendingMessage || uploadingImage}
            >
              {sendingMessage ? <CircularProgress size={20} /> : <Send />}
            </IconButton>
          </Box>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </Box>
      </ResponsiveModal>
    </Box>
  );
};

export default ChatManagement;
